import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateTime } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'

function AmazonTrafficDetailScreen() {
    const { queryId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [query, setQuery] = useState(null)
    const [queryLoading, setQueryLoading] = useState(true)
    const [error, setError] = useState('')

    const [rowsData, setRowsData] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const initialTableState = {
        visibility: {
            asin: true, sessions: true, pageViews: true,
            buyBoxPercentage: true, unitsOrdered: true,
            browserSessions: true, mobileAppSessions: true,
            periodStart: true, periodEnd: true,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_traffic_detail', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        asin: 'ASIN', sessions: 'Sessions', pageViews: 'Page Views',
        buyBoxPercentage: 'Buy Box %', unitsOrdered: 'Units Ordered',
        browserSessions: 'Browser Sessions', mobileAppSessions: 'Mobile Sessions',
        periodStart: 'Period Start', periodEnd: 'Period End',
    }

    useEffect(() => {
        const fetchQuery = async () => {
            setQueryLoading(true)
            try {
                const data = await amazonAPI.traffic.get(queryId)
                setQuery(data)
            } catch (err) {
                setError(formatErrorResponse(err))
            } finally {
                setQueryLoading(false)
            }
        }
        fetchQuery()
    }, [queryId])

    const fetchRows = useCallback(async () => {
        if (!query) return
        setRowsLoading(true)
        setError('')
        try {
            const params = {
                page: currentPage,
                pageSize: pageSize,
            }
            if (searchTerm) params.searchTerm = searchTerm
            if (sortColumn) {
                params.sortColumn = sortColumn
                params.sortDirection = sortDirection || 'asc'
            }

            const data = await amazonAPI.traffic.getRows(queryId, params)
            if (data && data.results !== undefined) {
                setRowsData(data.results)
                setTotalItems(data.count)
            } else {
                setRowsData(data || [])
                setTotalItems((data || []).length)
            }
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setRowsLoading(false) }
    }, [query, queryId, currentPage, pageSize, searchTerm, sortColumn, sortDirection])

    useEffect(() => { fetchRows() }, [fetchRows])

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => { setSearchTerm(value); setCurrentPage(1) }, 300)
    }
    const handleClearSearch = () => { setSearchInputValue(''); setSearchTerm(''); setCurrentPage(1) }

    const handleSort = (column, direction) => { setSortColumn(column); setSortDirection(direction); setCurrentPage(1) }

    const tableColumns = [
        columnVisibility.asin && { id: 'asin', header: 'ASIN', sortable: true, cell: (row) => row.asin ? <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> : '' },
        columnVisibility.sessions && { id: 'sessions', header: 'Sessions', sortable: true, cell: (row) => row.sessions != null ? row.sessions.toLocaleString() : '' },
        columnVisibility.pageViews && { id: 'pageViews', header: 'Page Views', sortable: true, cell: (row) => row.page_views != null ? row.page_views.toLocaleString() : '' },
        columnVisibility.buyBoxPercentage && { id: 'buyBoxPercentage', header: 'Buy Box %', sortable: true, cell: (row) => row.buy_box_percentage != null ? row.buy_box_percentage + '%' : '' },
        columnVisibility.unitsOrdered && { id: 'unitsOrdered', header: 'Units Ordered', sortable: true, cell: (row) => row.units_ordered != null ? row.units_ordered : '' },
        columnVisibility.browserSessions && { id: 'browserSessions', header: 'Browser Sessions', sortable: true, cell: (row) => row.browser_sessions != null ? row.browser_sessions : '' },
        columnVisibility.mobileAppSessions && { id: 'mobileAppSessions', header: 'Mobile Sessions', sortable: true, cell: (row) => row.mobile_app_sessions != null ? row.mobile_app_sessions : '' },
        columnVisibility.periodStart && { id: 'periodStart', header: 'Period Start', sortable: true, cell: (row) => row.period_start || '' },
        columnVisibility.periodEnd && { id: 'periodEnd', header: 'Period End', sortable: true, cell: (row) => row.period_end || '' },
    ].filter(Boolean)

    if (queryLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = query
        ? `${query.account_name || ''}${query.date_start && query.date_end ? ` (${query.date_start} - ${query.date_end})` : ''}`
        : ''

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">Traffic Data</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-traffic', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {rowsLoading && rowsData.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    <div className="d-flex align-items-center mb-3">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search ASIN..." />
                        </div>
                    </div>
                    {rowsData.length === 0 ? (
                        <p className="text-muted">No traffic data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={tableColumns} data={rowsData} tableId="amazon_traffic_detail"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        asin: 140, sessions: 110, pageViews: 110,
                                        buyBoxPercentage: 110, unitsOrdered: 120,
                                        browserSessions: 140, mobileAppSessions: 140,
                                        periodStart: 120, periodEnd: 120,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={setPageSize}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonTrafficDetailScreen
